import { useState } from 'react';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { sendEmailVerification } from 'firebase/auth';
import { getMarketplaceAuth } from '../../firebase';

interface LocationState {
  email?: string;
  ragione_sociale?: string;
  app_login_url?: string;
  email_verified?: boolean;
  signup_method?: 'email_password' | 'google';
}

export default function VerificaEmailPage() {
  const location = useLocation();
  const state = (location.state || {}) as LocationState;
  const { t } = useTranslation('signup');
  const { t: tCommon } = useTranslation('common');

  const [resending, setResending] = useState(false);
  const [resentOk, setResentOk] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  // Email verificata = utente arrivato da provider OAuth (Google) → non serve email
  const emailAlreadyVerified = state.email_verified === true;
  // Priorita: 1) URL passato dal wizard 2) env var VITE_APP_LOGIN_URL 3) fallback hardcoded
  const loginUrl =
    state.app_login_url ||
    (import.meta.env.VITE_APP_LOGIN_URL as string | undefined) ||
    'https://app.yumaos.com/login';

  const handleResend = async () => {
    const auth = getMarketplaceAuth();
    if (!auth.currentUser) {
      setResendError(t('verifica_email.errore_non_loggato'));
      return;
    }
    setResending(true);
    setResendError(null);
    try {
      await sendEmailVerification(auth.currentUser, {
        url: loginUrl + '?email_verified=1',
        handleCodeInApp: false,
      });
      setResentOk(true);
    } catch (err) {
      console.error('[VerificaEmailPage] resend failed', err);
      setResendError(t('verifica_email.errore_invio'));
    } finally {
      setResending(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Top bar */}
      <Box
        component="header"
        sx={{
          padding: { xs: '20px 24px', md: '24px 56px' },
          borderBottom: '1px solid var(--dust)',
        }}
      >
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono Variable", monospace',
            fontSize: '0.7rem',
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: 'var(--ink)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box
            component="span"
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: 'var(--sun)',
            }}
          />
          {tCommon('brand')}
        </Typography>
      </Box>

      <Container
        maxWidth="md"
        sx={{
          flex: 1,
          py: { xs: 6, md: 10 },
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Stack spacing={4} sx={{ width: '100%' }}>
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono Variable", monospace',
              fontSize: '0.7rem',
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: 'var(--sun-deep)',
            }}
          >
            {t('verifica_email.marker')}
          </Typography>

          <Typography
            variant="h1"
            sx={{
              fontFamily: '"Fraunces Variable", Georgia, serif',
              fontSize: { xs: '2.8rem', md: '4rem' },
              letterSpacing: '-0.02em',
              lineHeight: 1,
            }}
          >
            {emailAlreadyVerified
              ? t('verifica_email.gia_verificata_titolo')
              : t('verifica_email.titolo')}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: 'var(--ink-soft)',
              fontSize: '1.15rem',
              lineHeight: 1.55,
              maxWidth: 620,
            }}
          >
            {emailAlreadyVerified
              ? t('verifica_email.gia_verificata_descrizione', { email: state.email ?? '' })
              : t('verifica_email.descrizione', { email: state.email ?? '' })}
          </Typography>

          {emailAlreadyVerified ? (
            /* Caso utente Google: niente da verificare, link diretto al login */
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2.5}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              <Button
                variant="contained"
                size="large"
                component="a"
                href={loginUrl}
                sx={{
                  background: 'var(--ink)',
                  color: 'var(--bg)',
                  fontFamily: '"JetBrains Mono Variable", monospace',
                  fontSize: '0.78rem',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  padding: '16px 28px',
                  borderRadius: 0,
                  textDecoration: 'none',
                  '&:hover': { background: 'var(--sun-deep)' },
                  '& .arrow': {
                    display: 'inline-block',
                    marginLeft: '10px',
                    transition: 'transform 240ms',
                  },
                  '&:hover .arrow': { transform: 'translateX(8px)' },
                }}
              >
                <span>{t('verifica_email.cta_accedi')}</span>
                <span className="arrow">&rarr;</span>
              </Button>

              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono Variable", monospace',
                  fontSize: '0.75rem',
                  color: 'var(--ink-soft)',
                }}
              >
                {t('verifica_email.gia_verificata_hint')}
              </Typography>
            </Stack>
          ) : (
            /* Caso email/password: invitiamo a controllare la casella */
            <>
              <Box
                sx={{
                  padding: '20px 24px',
                  background: 'var(--bg-deep)',
                  border: '1px solid var(--dust)',
                  borderLeft: '3px solid var(--sun)',
                  maxWidth: 620,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"JetBrains Mono Variable", monospace',
                    fontSize: '0.78rem',
                    letterSpacing: '0.04em',
                    color: 'var(--ink)',
                    lineHeight: 1.6,
                  }}
                >
                  {t('verifica_email.suggerimento_spam')}
                </Typography>
              </Box>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2.5}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleResend}
                  disabled={resending || resentOk}
                  sx={{
                    background: 'var(--ink)',
                    color: 'var(--bg)',
                    fontFamily: '"JetBrains Mono Variable", monospace',
                    fontSize: '0.78rem',
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    padding: '16px 28px',
                    borderRadius: 0,
                    '&:hover': { background: 'var(--sun-deep)' },
                    '&.Mui-disabled': {
                      background: 'var(--dust)',
                      color: 'var(--bg-deep)',
                    },
                  }}
                >
                  {resentOk
                    ? t('verifica_email.cta_inviato')
                    : resending
                      ? t('verifica_email.cta_invio')
                      : t('verifica_email.cta_reinvia')}
                </Button>

                <Typography
                  sx={{
                    fontFamily: '"JetBrains Mono Variable", monospace',
                    fontSize: '0.75rem',
                    color: 'var(--ink-soft)',
                  }}
                >
                  {t('verifica_email.dopo_click')}
                </Typography>
              </Stack>

              {resendError && (
                <Typography sx={{ color: '#B33A2E', fontSize: '0.9rem' }}>
                  {resendError}
                </Typography>
              )}
            </>
          )}
        </Stack>
      </Container>
    </Box>
  );
}
